import { colorOption } from '@/data/optionsConstants';
import moment from 'moment';

export function getOid(v) {
  return v && v._id ? v._id : v;
}

export { getOid as getId };

export function oidEquals(a, b) {
  a = getOid(a);
  b = getOid(b);
  return a === b || (a == null && b == null) || String(a) === String(b);
}

/**
 * Opens a print window with the content of a specified element (auto-closes after print)
 * @param {string} id - The ID of the element to print (default: 'print')
 * @param {number} width - Window width in pixels (default: 1123 for A4 landscape)
 * @param {number} height - Window height in pixels (default: 794 for A4 landscape)
 */
export function openPrintWindow(id = 'print', width = 1123, height = 794) {
  try {
    // Get HTML to print from element
    const printElement = document.getElementById(id);
    if (!printElement) {
      throw new Error(`Element with id "${id}" not found`);
    }
    const prtHtml = printElement.innerHTML;

    // Get all stylesheets HTML
    let stylesHtml = '';
    for (const node of [...document.querySelectorAll('link[rel="stylesheet"], style')]) {
      stylesHtml += node.outerHTML;
    }

    // Open the print window with A4 landscape dimensions
    const WinPrint = window.open(
      '',
      '',
      `left=0,top=0,width=${width},height=${height},toolbar=0,scrollbars=0,status=0`,
    );

    if (!WinPrint) {
      throw new Error('Failed to open print window. Pop-ups may be blocked.');
    }

    WinPrint.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta http-equiv="Cache-Control" content="no-cache, no-store, must-revalidate" />
    <meta http-equiv="Pragma" content="no-cache" />
    <meta http-equiv="Expires" content="0" />
    <link rel="stylesheet" type="text/css" media="print" href="${process.env.BASE_URL}/assets/print.css">
    ${stylesHtml}
  </head>
  <body id="html">
    <script>
      var isIpad = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 0) || navigator.platform === 'iPad';
      
      if (isIpad) {
        // Wait for DOM to be ready
        setTimeout(function() {
          var pageElements = document.getElementsByClassName("page");
          for (var i = 0; i < pageElements.length; i++) {
            pageElements[i].classList.add("onIpad");
          }
        }, 100);
      }

      // Auto-close window after printing
      window.onafterprint = function() {
        window.close();
      };

      // Fallback for browsers that don't support onafterprint
      // Close window if user cancels print dialog (detect focus change)
      var printDialogClosed = false;
      window.onfocus = function() {
        if (!printDialogClosed) {
          printDialogClosed = true;
          // Give a small delay to check if print was successful
          setTimeout(function() {
            window.close();
          }, 500);
        }
      };
    </script>
    ${prtHtml}
  </body>
</html>`);

    WinPrint.document.close();

    // Wait for all resources to load before printing
    WinPrint.onload = function () {
      WinPrint.focus();
      WinPrint.print();
    };
  } catch (error) {
    console.error('Print window error:', error);
    alert('Failed to open print preview. Please try again.');
  }
}

/**
 * Print using iframe (no popup dialog)
 * @param {string} id - The ID of the element to print
 */
export function printWithIframe(id = 'print') {
  try {
    // Get HTML to print from element
    const printElement = document.getElementById(id);
    if (!printElement) {
      throw new Error(`Element with id "${id}" not found`);
    }
    const prtHtml = printElement.innerHTML;

    // Get all stylesheets HTML
    let stylesHtml = '';
    for (const node of [...document.querySelectorAll('link[rel="stylesheet"], style')]) {
      stylesHtml += node.outerHTML;
    }

    // Create hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`<!DOCTYPE html>
<html>
  <head>
    <meta http-equiv="Cache-Control" content="no-cache, no-store, must-revalidate" />
    <meta http-equiv="Pragma" content="no-cache" />
    <meta http-equiv="Expires" content="0" />
    <link rel="stylesheet" type="text/css" media="print" href="${process.env.BASE_URL}/assets/print.css">
    ${stylesHtml}
  </head>
  <body>
    <script>
      var isIpad = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 0) || navigator.platform === 'iPad';
      
      if (isIpad) {
        setTimeout(function() {
          var pageElements = document.getElementsByClassName("page");
          for (var i = 0; i < pageElements.length; i++) {
            pageElements[i].classList.add("onIpad");
          }
        }, 100);
      }
    </script>
    ${prtHtml}
  </body>
</html>`);
    iframeDoc.close();

    // Wait for content to load then print
    iframe.onload = function () {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        // Remove iframe after print dialog is closed
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 100);
    };
  } catch (error) {
    console.error('Print iframe error:', error);
    alert('Failed to print. Please try again.');
  }
}

export function printYOMT(yomtData) {
  try {
    if (!yomtData) {
      throw new Error('YOMT data is required');
    }

    // Helper function to format money
    const formatMoney = number => {
      if (number == null || isNaN(number)) return '0';
      const parts = number.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    };

    // Helper function to format date
    const formatDate = date => {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Calculate totals
    const services = yomtData.services || [];
    let totalPrice1 = 0;
    let totalPrice2 = 0;

    services.forEach(service => {
      totalPrice1 += service.price1 || 0;
      totalPrice2 += service.price2 || 0;
    });

    const totalPrice = totalPrice1 + totalPrice2;

    // Generate services rows
    let servicesRows = '';
    services.forEach(service => {
      servicesRows += `
        <tr>
          <td>${service.name}</td>
          <td>$${formatMoney(service.price1 || 0)}</td>
        </tr>
      `;
    });

    // Add 手續費 row (sum of price2)
    servicesRows += `
      <tr>
        <td>手續費</td>
        <td>$${formatMoney(totalPrice2)}</td>
      </tr>
    `;

    // Add total row
    servicesRows += `
      <tr>
        <td style="text-align: right"><strong>總金額: </strong></td>
        <td><span>$${formatMoney(totalPrice)}</span></td>
      </tr>
    `;

    // Payment methods
    const paymentMethods = (yomtData.payment?.paymentMethods || []).join(', ');

    // Payment status
    let paymentStatus = '';
    if (yomtData.payment?.paid) {
      const paidDate = formatDate(yomtData.payment.paidDate);
      paymentStatus = `${paidDate} 已付款`;
    } else {
      paymentStatus = '尚未付款';
    }

    // Others list (待補文件)
    const othersList = (yomtData.othersList || []).join(', ');

    // Payment remarks labels mapping
    const paymentRemarkLabels = {
      vehicle_reassignment_registration_number: '車輛改配登記號碼',
      sold_plate_number: '售出車牌號碼',
      cheque_details: '支票明細',
      deposit_paid: '已支付按金',
    };

    // Payment remarks
    let paymentRemarks = '';
    if (yomtData.payment?.remarks) {
      const remarks = yomtData.payment.remarks;
      const remarksList = [];

      // Iterate through all payment remark options
      Object.keys(paymentRemarkLabels).forEach(key => {
        if (remarks[key]?.selected) {
          remarksList.push(`${paymentRemarkLabels[key]} : ${remarks[key].detail || ''}`);
        }
      });

      paymentRemarks = remarksList.join('<br>');
    }

    // Build isTel remark
    const isTelRemark = yomtData.isTel ? '已透過運輸署網頁核實客戶電話號碼' : '';

    // Combine all remarks for display
    const combinedRemarks = [isTelRemark, paymentRemarks, yomtData.remark2 || '']
      .filter(r => r)
      .join('<br>');

    // Build the invoice HTML (two columns - left and right)
    const buildInvoiceColumn = () => `
      <div class="ptop">
        <h2>
          <span class="pright minw">正本發票</span>
          <div class="header-title" style="display: inline-block; vertical-align: middle; margin-bottom: 5px;">
            <div>佑安汽車服務公司</div>
            <div>佑安保險代理公司</div>
          </div>
        </h2>
        <dl>
          <dd>地址：新界粉嶺聯和墟聯興街35D地下</dd>
          <dd>電話：2669 8555/ 2386 3161/ 9373 1297</dd>
          <dd>WhatsApp: 6696 7236/ 9373 1297</dd>
          <dd>WeChat: 93731297</dd>
        </dl>
      </div>
      <div class="pdate">
        <div class="pleft">
          <ul class="showd">
            <li><span>日期: ${formatDate(yomtData.payDate)}</span></li>
            <li><span>經辦人: </span>${(yomtData.createdBy && yomtData.createdBy.name) ? yomtData.createdBy.name : ''}</li>
          </ul>
        </div>
        <div class="pright">
          <ul class="showd minw">
            <li><span>車牌號碼: </span><br /><span>${yomtData.vehno || ''}</span></li>
          </ul>
        </div>
        <div class="clear"></div>
      </div>
      <div class="puser">
        <div class="pleft">
          <ul class="showd">
            <li><span>客戶: </span>${yomtData.customer || ''}</li>
            <li><span>代理人: </span>${yomtData.agent || ''}</li>
          </ul>
        </div>
        <div class="pright">
          <ul class="showd minw">
            <li><span>電話: ${yomtData.customerTel || ''}</span></li>
            <li><span>電話: ${yomtData.agentTel || ''}</span></li>
          </ul>
        </div>
        <div class="clear"></div>
      </div>
      <div class="table">
        <table border="0" cellspacing="0" class="layui-table services-table">
          <colgroup>
            <col style="width: 80%" />
            <col style="width: 20%" />
          </colgroup>
          <thead>
            <tr>
              <th width="80%">代辦服務及費用</th>
              <th width="20%"></th>
            </tr>
          </thead>
          <tbody class="servicesList">
            ${servicesRows}
          </tbody>
        </table>
      </div>
      <div class="pways">
        <ul class="showd">
          <li style="float: left"><span>付款方式: </span><br />${paymentMethods}</li>
          <li style="float: right; width: 30%; text-align: left">
            <span>付款狀態: </span><br />${paymentStatus}
          </li>
          <div class="clear"></div>
        </ul>
      </div>
      <div class="premark">
        <div class="premark-row">
          <ul class="showd premark-docs">
            <li><span>待補文件: </span><br />${othersList}</li>
          </ul>
          <div class="premark-qr">
            <div class="qr-item">
              <div class="qr-title">轉數快付款</div>
              <img
                class="qr-image"
                src="${window.location.origin}${process.env.BASE_URL}assets/qrCode/fps_payment.svg"
                onerror="this.style.display='none'"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="premark">
        <ul class="showd">
          <li><span>備註: </span><br /><em>${combinedRemarks}</em></li>
        </ul>
      </div>
    `;

    const printHtml = `<!DOCTYPE html>
<html class="x-admin-sm">
  <head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
    <title>YOMT 列印</title>
    <meta name="renderer" content="webkit" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge,chrome=1" />
    <meta name="viewport" content="width=device-width,user-scalable=yes, minimum-scale=0.4, initial-scale=0.8,target-densitydpi=low-dpi" />
    <link rel="stylesheet" href="${process.env.BASE_URL}/assets/layui.css" />
    <style>
      body {
        background: #ffffff;
        font-family: Arial, "Microsoft YaHei", sans-serif;
        margin: 0;
        padding: 20px;
      }
      .layui-table {
        border: 1px solid #333;
        border-collapse: collapse !important;
        width: 100%;
      }
      /* Force consistent column widths between the left and right invoice copies */
      .services-table {
        table-layout: fixed;
      }
      .services-table th:nth-child(2),
      .services-table td:nth-child(2) {
        text-align: right;
        white-space: nowrap;
      }
      .layui-table td {
        font-size: 15px;
        border: 1px solid #333;
        padding: 2px 5px;
      }
      .layui-table th {
        font-size: 15px;
        font-weight: bold;
        border: 1px solid #333;
        padding: 2px 5px;
      }
      .servicesList span {
        font-size: 15px;
        font-weight: bold;
      }
      ul.showd {
        font-size: 15px;
        margin-left: 5px;
        list-style: none;
        padding: 0;
      }
      ul.showd li {
        line-height: 20px;
        margin: 5px 0px;
      }
      ul.showd li span {
        font-weight: bold;
      }
      ul.showd li em {
        font-size: 12px;
        font-style: normal;
      }
      .pleft {
        float: left;
      }
      .pright {
        float: right;
      }
      .ptop {
        margin-bottom: 5px;
        margin-left: 5px;
      }
      .ptop h2 {
        font-size: 16px;
        font-weight: bold;
        margin: 0;
      }
      .ptop h2 span {
        font-size: 20px;
        color: #ccc;
      }
      .header-title {
        line-height: 1.1;
        font-size: 22px;
        font-weight: bold;
      }
      .ptop dl {
        margin-top: 2px;
      }
      .ptop dl dt {
        font-size: 16px;
        font-weight: bold;
      }
      .ptop dl dd {
        font-size: 12px;
        line-height: 15px;
        margin: 0;
      }
      .puser,
      .pways,
      .premark {
        margin-top: 2px;
        margin-bottom: 2px;
      }
      .premark-row {
        display: grid;
        grid-template-columns: 1fr 2.3cm;
        column-gap: 6px;
        width: 100%;
        align-items: start;
      }
      .premark-docs {
        min-width: 0;
        padding-right: 4px;
        box-sizing: border-box;
        overflow-wrap: break-word;
        word-break: break-word;
      }
      .premark-qr {
        width: 2.3cm;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        justify-self: end;
      }
      .qr-item {
        text-align: center;
      }
      .qr-title {
        font-size: 11px;
        font-weight: bold;
        margin-bottom: 2px;
        color: #666;
      }
      .fps-desc {
        font-size: 10px;
        color: #333;
        margin-top: 4px;
        margin-bottom: 2px;
        line-height: 1.4;
        text-align: right;
        white-space: nowrap;
        clear: both;
      }
      .qr-image {
        width: 2.3cm;
        height: 2.3cm;
        min-width: 2.3cm;
        min-height: 2.3cm;
        object-fit: contain;
        border: none;
      }
      .minw {
        min-width: 150px;
        text-align: left;
      }
      .clear {
        clear: both;
      }
      .pdate {
        border-bottom: 2px solid #ccc;
        padding-bottom: 2px;
      }
      .pwhole {
        width: 100%;
      }
      .invoice-column {
        float: left;
        width: 46%;
      }
      .invoice-column:first-child {
        margin-right: 8%;
      }
      
      @media print {
        body {
          padding: 10px;
        }
        /* Ensure header background prints correctly */
        .layui-table thead tr {
          background-color: #BEBEBE !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    </style>
  </head>
  <body>
    <div class="pwhole">
      <div class="invoice-column">
        ${buildInvoiceColumn()}
      </div>
      <div class="invoice-column">
        ${buildInvoiceColumn()}
      </div>
      <div class="clear"></div>
    </div>
  </body>
</html>`;

    // Create hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printHtml);
    iframeDoc.close();

    // Wait for content to load then print
    iframe.onload = function () {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        // Remove iframe after print dialog is closed
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 100);
    };
  } catch (error) {
    console.error('Print YOMT error:', error);
    alert('列印失敗，請重試。');
  }
}

export function printLPMS(lpmsData) {
  try {
    if (!lpmsData) {
      throw new Error('LPMS data is required');
    }

    // Helper function to format date
    const formatDate = date => {
      if (!date) return '';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const printHtml = `<!DOCTYPE html>
<html class="x-admin-sm">
  <head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
    <title>LPMS 列印</title>
    <meta name="renderer" content="webkit" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge,chrome=1" />
    <meta name="viewport" content="width=device-width,user-scalable=yes, minimum-scale=0.4, initial-scale=0.8,target-densitydpi=low-dpi" />
    <link rel="stylesheet" href="${process.env.BASE_URL}/assets/layui.css" />
    <style>
      body {
        background: #ffffff;
        font-family: Arial, "Microsoft YaHei", sans-serif;
        margin: 0;
        padding: 20px;
      }
      .layui-form-label {
        font-size: 14px;
      }
      .layui-form-radio {
        padding-right: 0px;
        margin-left: 5px;
      }
      .x-admin-sm .layui-table td {
        font-size: 18px;
      }
      .x-admin-sm .layui-table th {
        font-size: 20px;
        font-weight: bold;
      }
      .x-admin-sm .layui-table dl {
        margin: 5px 0px 10px 0px;
      }
      .x-admin-sm .layui-table dt {
        font-size: 18px;
        font-weight: bold;
      }
      .x-admin-sm .layui-table dd {
        font-size: 18px;
        line-height: 25px;
        cursor: pointer;
      }
      .x-admin-sm .layui-table dd.red {
        color: red;
        font-weight: bold;
      }
      #servicesList span {
        font-size: 20px;
        font-weight: bold;
      }
      ul.showd {
        font-size: 20px;
        margin-left: 20px;
      }
      ul.showd li {
        margin: 5px 0px;
      }
      ul.showd li span {
        font-weight: bold;
      }
      .table {
        margin-bottom: 20px;
      }
    </style>
  </head>
  <body>
    <div class="layui-fluid">
      <div class="layui-row">
        <div class="table">
          <table border="0" cellspacing="0" class="layui-table">
            <thead>
              <tr>
                <th>車牌號碼</th>
                <th>客戶名稱</th>
                <th>編號</th>
                <th>狀態</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${lpmsData.vehno || ''}</td>
                <td>${lpmsData.customer || ''}</td>
                <td>${lpmsData.businessno || ''}</td>
                <td>${lpmsData.state || ''}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="table">
          <table border="0" cellspacing="0" class="layui-table">
            <thead>
              <tr>
                <th>發出日期</th>
                <th>到期日</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${formatDate(lpmsData.inputDate)}</td>
                <td>${formatDate(lpmsData.outDate)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </body>
</html>`;

    // Create hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printHtml);
    iframeDoc.close();

    // Wait for content to load then print
    iframe.onload = function () {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();

        // Remove iframe after print dialog is closed
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 100);
    };
  } catch (error) {
    console.error('Print LPMS error:', error);
    alert('列印失敗，請重試。');
  }
}

export function colorProportion(cp) {
  return [formatRound(cp.firstRound), formatRound(cp.secondRound)].filter(x => x).join(' ~~> \n');
}

function formatRound(round) {
  if (!round) return '';
  return ['1', '2', '3']
    .map(x => {
      const type = round['type' + x];
      const cX = round['color' + x] || [];
      const uX = round['unit' + x] || [];
      const seq = cX.map((c, i) => c + (uX[i] || '')).join(' + ');
      const typeFormat = {
        hairFront: '(前) {seq}',
        hairTail: '(尾) {seq}',
        full: '(全) {seq}',
        hairRoot: '(根) {seq}',
        washAndDye: '洗補 {seq}',
        top: '(頂) {seq}',
      };
      return (typeFormat[type] || '{seq}').replace('{seq}', seq);
    })
    .filter(x => x)
    .join(' ');
}

export function emptyPaginationResult() {
  return { docs: [], limit: 10, offset: 0, page: 1, pages: 1, total: 0 };
}

// counts include start and end date
export function calculateBNWorkingDays(start, end, publicHolidayDates) {
  let count = 0;
  if (!(start && end) || !publicHolidayDates || publicHolidayDates.length === 0) {
    return null;
  }

  let startDate = moment(start).startOf('day');
  let endDate = moment(end).endOf('day');
  const publicHolidays = publicHolidayDates;

  //return if start date exceed the last day of the year provided by public holidays data
  if (endDate.isAfter(moment(publicHolidays.slice(-1)[0].start).endOf('year'))) {
    return null;
  }

  count = endDate.diff(startDate, 'days') + 1;

  for (let i = startDate; i <= endDate; i.add(1, 'days')) {
    // filter out sunday
    if (i.day() === 0) {
      count -= 1;
      continue;
    }
    // filter out public holidays
    for (let j = 0; j < publicHolidays.length - 1; j++) {
      if (moment(i).isBetween(publicHolidays[j].start, publicHolidays[j].end, null, '[)')) {
        count -= 1;
      }
    }
  }
  return isNaN(count) ? 0 : count;
}

export function calculateBNWorkingHours(startDate, startTime, endTime) {
  if (!(startDate && startTime && endTime)) {
    return null;
  }
  let dateStart = new Date(`${startDate} ${startTime}`);
  let dateEnd = new Date(`${startDate} ${endTime}`);
  let start = moment(dateStart);
  let end = moment(dateEnd);
  const count = end.diff(start, 'hours');

  return isNaN(count) ? 0 : count;
}

export async function showPdf(api, url, isPrint = false) {
  const pdfArrayBuffer = (await api.get(url, { responseType: 'arraybuffer' })).data;
  const blobPdf = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
  const blobURL = URL.createObjectURL(blobPdf);
  if (isPrint) {
    const win = window.open(
      blobURL,
      '',
      'left=0,top=0,width=594,height=824,toolbar=0,scrollbars=0,status=0',
    );
    setTimeout(() => {
      win.focus();
      win.print();
    }, 500);
  } else {
    const win = window.open(blobURL);
  }
}
